import type { MaterialsParams, MaterialsQueryInput } from '../-types';

/** The three fields the materials list maps into its `ORDER BY`. */
const SORTABLE_FIELDS = new Set(['name', 'category', 'updatedAt']);

/** The three columns the route will filter on. */
const FILTERABLE_FIELDS = new Set(['name', 'category', 'description']);

/** The route caps `q` at 255 characters. */
const SEARCH_MAX = 255;

/**
 * Turns table state into the endpoint's parameters.
 *
 * Single conversion point, as on the other management lists: the query key is
 * what this returns, so the route loader (reading the URL) and the page's hook
 * (reading `useTable`) only share a cache entry if both derive their params the
 * same way.
 *
 * Deliberately absent: `isDirectoryList`. The old page used to send it, but
 * the route's querystring schema does not declare it and strips it before the
 * handler runs, so it would do nothing here. (The standards list is the one that genuinely needs it — same
 * parameter name, different route.)
 */
export function toMaterialsParams(input: MaterialsQueryInput): MaterialsParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (FILTERABLE_FIELDS.has(key) && values && values.length > 0) {
      filter[key] = values.join(',');
    }
  }
  const hasFilter = Object.keys(filter).length > 0;

  // Dropped rather than forwarded: an unsupported field would 400 the list.
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  const q = (input.search ?? '').trim().slice(0, SEARCH_MAX);

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(q ? { q } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

export function buildMaterialsQs(params: MaterialsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.q) qs.set('q', params.q);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
